import type { StyleSpecification, SourceSpecification, LayerSpecification } from 'maplibre-gl';
import type { CapaBase } from '../app/estado';

/**
 * Fuentes y capas del mapa: servicios publicos del IGN/IDEE y del Catastro consumidos
 * directamente por el navegador (todos permiten CORS `*`, comprobado 2026-09-22) y las
 * fuentes GeoJSON locales que rellena la aplicacion.
 */
const ATRIBUCION_IGN = '<a href="https://www.ign.es" target="_blank" rel="noopener">© Instituto Geográfico Nacional</a>';
const ATRIBUCION_CATASTRO = '<a href="https://www.sedecatastro.gob.es" target="_blank" rel="noopener">© Dirección General del Catastro</a>';

function wmts(servicio: string, capa: string, formato: 'jpeg' | 'png'): string {
  return `https://www.ign.es/wmts/${servicio}?service=WMTS&request=GetTile&version=1.0.0&layer=${capa}&style=default`
    + `&format=image/${formato}&tilematrixset=GoogleMapsCompatible&tilematrix={z}&tilerow={y}&tilecol={x}`;
}

export const URL_WMS_CATASTRO = 'https://ovc.catastro.meh.es/Cartografia/WMS/ServidorWMS.aspx?SERVICE=WMS&VERSION=1.1.1&REQUEST=GetMap'
  + '&LAYERS=Catastro&STYLES=&SRS=EPSG:3857&BBOX={bbox-epsg-3857}&WIDTH=512&HEIGHT=512&FORMAT=image/png&TRANSPARENT=true';

export const URL_WMS_HISTORICO = 'https://www.ign.es/wms/pnoa-historico?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap'
  + '&LAYERS={capa}&STYLES=&CRS=EPSG:3857&BBOX={bbox-epsg-3857}&WIDTH=512&HEIGHT=512&FORMAT=image/jpeg';

export const URL_TERRENO = 'https://xyz-mdt.idee.es/1.0.0/raster-dem/{z}/{x}/{y}.png';

/** Anos disponibles en el WMS pnoa-historico (GetCapabilities del 2026-09-22). */
export const ANOS_HISTORICO: string[] = Array.from({ length: 20 }, (_, i) => `PNOA${2024 - i}`);

export const CAPAS_BASE: Record<CapaBase, { id: string; nombre: string }> = {
  ign: { id: 'base-ign', nombre: 'Mapa IGN' },
  orto: { id: 'base-orto', nombre: 'Ortofoto PNOA' },
  mtn: { id: 'base-mtn', nombre: 'MTN raster' },
  lidar: { id: 'base-lidar', nombre: 'Relieve LiDAR' },
};

function raster(tiles: string[], atribucion: string, maxzoom = 20, tileSize = 256): SourceSpecification {
  return { type: 'raster', tiles, tileSize, maxzoom, attribution: atribucion };
}

const vacio = (): { type: 'FeatureCollection'; features: never[] } => ({ type: 'FeatureCollection', features: [] });

export function estiloInicial(): StyleSpecification {
  const sources: Record<string, SourceSpecification> = {
    'src-ign': raster([wmts('ign-base', 'IGNBaseTodo', 'jpeg')], ATRIBUCION_IGN),
    'src-orto': raster([wmts('pnoa-ma', 'OI.OrthoimageCoverage', 'jpeg')], ATRIBUCION_IGN),
    'src-mtn': raster([wmts('mapa-raster', 'MTN', 'jpeg')], ATRIBUCION_IGN),
    'src-lidar': raster(['https://wmts-mapa-lidar.idee.es/lidar?service=WMTS&request=GetTile&version=1.0.0&layer=EL.GridCoverageDSM&style=default&format=image/png&tilematrixset=GoogleMapsCompatible&tilematrix={z}&tilerow={y}&tilecol={x}'], ATRIBUCION_IGN, 17),
    'src-rotulos': raster([wmts('ign-base', 'IGNBaseOrto', 'png')], ATRIBUCION_IGN),
    'src-historico': raster([URL_WMS_HISTORICO.replace('{capa}', 'PNOA2010')], ATRIBUCION_IGN, 19, 512),
    'src-catastro': raster([URL_WMS_CATASTRO], ATRIBUCION_CATASTRO, 20, 512),
    'src-terreno': { type: 'raster-dem', tiles: [URL_TERRENO], tileSize: 256, encoding: 'mapbox', maxzoom: 15, attribution: ATRIBUCION_IGN },
    'src-admin': { type: 'vector', tiles: ['https://vt-unidades-administrativas.ign.es/1.0.0/uadministrativa/{z}/{x}/{y}.pbf'], maxzoom: 14, attribution: ATRIBUCION_IGN },
    agua: { type: 'geojson', data: vacio(), promoteId: 'id' },
    zona: { type: 'geojson', data: vacio(), promoteId: 'rc' },
    partes: { type: 'geojson', data: vacio(), promoteId: 'id' },
    dibujo: { type: 'geojson', data: vacio() },
    medicion: { type: 'geojson', data: vacio() },
    puntos: { type: 'geojson', data: vacio() },
  };

  const layers: LayerSpecification[] = [
    { id: 'base-ign', type: 'raster', source: 'src-ign', layout: { visibility: 'visible' } },
    { id: 'base-orto', type: 'raster', source: 'src-orto', layout: { visibility: 'none' } },
    { id: 'base-mtn', type: 'raster', source: 'src-mtn', layout: { visibility: 'none' } },
    { id: 'base-lidar', type: 'raster', source: 'src-lidar', layout: { visibility: 'none' } },
    { id: 'historico', type: 'raster', source: 'src-historico', layout: { visibility: 'none' } },
    { id: 'rotulos', type: 'raster', source: 'src-rotulos', layout: { visibility: 'none' } },
    { id: 'catastro-wms', type: 'raster', source: 'src-catastro', minzoom: 13, paint: { 'raster-opacity': 0.7 } },
    // Rios y arroyos del IGN (WFS de hidrografia de la IDEE) cargados con la zona.
    {
      id: 'agua-line', type: 'line', source: 'agua',
      paint: {
        'line-color': '#0b7dd6',
        'line-width': ['interpolate', ['linear'], ['zoom'], 10, 1, 14, 2, 18, 4],
        'line-opacity': ['case', ['==', ['get', 'persistencia'], 'perennial'], 0.95, 0.6],
      },
    },
    {
      id: 'agua-etiquetas', type: 'symbol', source: 'agua', minzoom: 13,
      layout: { 'symbol-placement': 'line', 'text-field': ['get', 'etiqueta'], 'text-size': 11, 'text-letter-spacing': 0.05 },
      paint: { 'text-color': '#0b5aa0', 'text-halo-color': '#ffffff', 'text-halo-width': 1.2 },
    },
    {
      id: 'zona-fill', type: 'fill', source: 'zona',
      paint: {
        'fill-color': [
          'case',
          ['boolean', ['feature-state', 'seleccionada'], false], '#e53e3e',
          ['boolean', ['get', 'esCandidato'], false], '#dd6b20',
          ['boolean', ['feature-state', 'resultado'], false], '#ecc94b',
          ['boolean', ['feature-state', 'hover'], false], '#63b3ed',
          '#3182ce',
        ],
        'fill-opacity': [
          'case',
          ['boolean', ['feature-state', 'seleccionada'], false], 0.55,
          ['boolean', ['get', 'esCandidato'], false], 0.55,
          ['boolean', ['feature-state', 'resultado'], false], 0.55,
          ['boolean', ['feature-state', 'hover'], false], 0.35,
          0.04,
        ],
      },
    },
    {
      id: 'zona-line', type: 'line', source: 'zona',
      paint: {
        'line-color': ['case', ['boolean', ['feature-state', 'seleccionada'], false], '#c53030', ['boolean', ['get', 'esCandidato'], false], '#c05621', '#2b6cb0'],
        'line-width': ['case', ['boolean', ['feature-state', 'seleccionada'], false], 3, ['boolean', ['feature-state', 'resultado'], false], 2, 0.8],
      },
    },
    {
      id: 'zona-etiquetas', type: 'symbol', source: 'zona', minzoom: 17,
      layout: { 'text-field': ['get', 'etiqueta'], 'text-size': 11, 'text-allow-overlap': false },
      paint: { 'text-color': '#1a365d', 'text-halo-color': '#ffffff', 'text-halo-width': 1.2 },
    },
    {
      id: 'partes-3d', type: 'fill-extrusion', source: 'partes', minzoom: 14, layout: { visibility: 'none' },
      paint: {
        'fill-extrusion-color': [
          'case',
          ['boolean', ['get', 'esCandidato'], false], '#dd6b20',
          ['match', ['get', 'uso'], '1_residential', '#a0aec0', '4_2_retail', '#f6ad55', '4_1_office', '#90cdf4', '3_industrial', '#b794f4', '4_3_publicServices', '#9ae6b4', '2_agriculture', '#c6f6d5', '#cbd5e0'],
        ],
        'fill-extrusion-height': ['get', 'altura'],
        'fill-extrusion-base': 0,
        'fill-extrusion-opacity': 0.9,
      },
    },
    { id: 'dibujo-fill', type: 'fill', source: 'dibujo', paint: { 'fill-color': '#2b6cb0', 'fill-opacity': 0.12 } },
    { id: 'dibujo-line', type: 'line', source: 'dibujo', paint: { 'line-color': '#2b6cb0', 'line-width': 2, 'line-dasharray': [2, 1] } },
    { id: 'medicion-line', type: 'line', source: 'medicion', paint: { 'line-color': '#d53f8c', 'line-width': 2.5 } },
    { id: 'medicion-fill', type: 'fill', source: 'medicion', paint: { 'fill-color': '#d53f8c', 'fill-opacity': 0.1 } },
    { id: 'medicion-puntos', type: 'circle', source: 'medicion', filter: ['==', '$type', 'Point'], paint: { 'circle-radius': 4, 'circle-color': '#ffffff', 'circle-stroke-color': '#d53f8c', 'circle-stroke-width': 2 } },
    { id: 'puntos', type: 'circle', source: 'puntos', paint: { 'circle-radius': 7, 'circle-color': '#805ad5', 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 2 } },
  ];

  // Las teselas nacionales (si hay) van bajo la capa vectorial de la zona cargada y sobre el WMS.
  const pm = fuentesPmtiles();
  Object.assign(sources, pm.sources);
  const posicion = layers.findIndex((l) => l.id === 'zona-fill');
  layers.splice(posicion, 0, ...pm.layers);
  return { version: 8, glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf', sources, layers, sky: {} };
}

export const IDS_BASE = Object.values(CAPAS_BASE).map((c) => c.id);

/**
 * Teselas vectoriales nacionales del Catastro (fase 5), generadas por lotes/catastro_atom.py y
 * publicadas en el bucket como PMTiles. Solo se anaden si VITE_PMTILES_BASE apunta a la carpeta
 * que contiene parcelas.pmtiles y edificios.pmtiles (se leen por rangos HTTP).
 */
export const BASE_PMTILES = (import.meta.env.VITE_PMTILES_BASE as string | undefined)?.replace(/\/$/, '') ?? null;

export function fuentesPmtiles(): { sources: Record<string, SourceSpecification>; layers: LayerSpecification[] } {
  if (!BASE_PMTILES) return { sources: {}, layers: [] };
  return {
    sources: {
      'pm-parcelas': { type: 'vector', url: `pmtiles://${BASE_PMTILES}/parcelas.pmtiles`, attribution: ATRIBUCION_CATASTRO },
      'pm-edificios': { type: 'vector', url: `pmtiles://${BASE_PMTILES}/edificios.pmtiles`, attribution: ATRIBUCION_CATASTRO },
    },
    layers: [
      { id: 'pm-parcelas-line', type: 'line', source: 'pm-parcelas', 'source-layer': 'parcelas', minzoom: 13, paint: { 'line-color': '#4a5568', 'line-width': 0.6, 'line-opacity': 0.8 } },
      { id: 'pm-edificios-fill', type: 'fill', source: 'pm-edificios', 'source-layer': 'edificios', minzoom: 14, paint: { 'fill-color': '#a0aec0', 'fill-opacity': 0.35 } },
    ],
  };
}
